import { Input } from '@bs/ui-components'
import { Check } from 'lucide-react'

import { SearchPicker, type SearchPickerOption } from '@/components/ui/search-picker'
import { type PreferenceOption } from '@/features/profile/types'
import { availabilityOptions, type AvailabilityId, type JobPreferencesView } from '@/features/profile/types'
import { getCities } from '@/services/metadata'

/** `bs-core.world_cities` → the picker's generic option shape (`id` is the cityId, stringified). */
function citiesToOptions(cities: Awaited<ReturnType<typeof getCities>>): SearchPickerOption[] {
  return cities.map((city) => ({ id: String(city.cityId), label: city.city }))
}

export type JobPreferencesSection = 'about-you' | 'about-job' | 'about-company'

/**
 * Purely presentational — the parent (`Preferences` in `profile-page.tsx`, or the onboarding
 * wizard) fetches and owns the data. Fetching lives up there because the read-only summary view
 * needs the SAME elId↔label maps this editor needs, and fetching twice for one screen would be
 * wasteful.
 */
export interface JobPreferencesOptions {
  workspaceType: PreferenceOption[]
  seniorityLevel: PreferenceOption[]
  jobTitle: PreferenceOption[]
  skill: PreferenceOption[]
  jobType: PreferenceOption[]
  location: PreferenceOption[]
  companySize: PreferenceOption[]
  industry: PreferenceOption[]
  currency: PreferenceOption[]
}

export function JobPreferencesEditor({
  value,
  onChange,
  section,
  availability,
  onAvailabilityChange,
  options,
  onLocationResultsSeen,
}: {
  value: JobPreferencesView
  onChange(value: JobPreferencesView): void
  section: JobPreferencesSection
  availability: AvailabilityId
  onAvailabilityChange(value: AvailabilityId): void
  options: JobPreferencesOptions
  /**
   * "Desired Location" is a live debounced city search (`bs-core.world_cities`, 722 rows —
   * matches legacy's `useLocationsSearchQuery`, which never preloaded its full table either).
   * Every result batch the picker shows is reported here so the parent can keep those cities
   * id↔label resolvable after selection.
   */
  onLocationResultsSeen(cities: SearchPickerOption[]): void
}) {
  const update = <K extends keyof JobPreferencesView>(key: K, next: JobPreferencesView[K]) =>
    onChange({ ...value, [key]: next })

  return (
    <div>
      {section === 'about-you' ? <PreferenceGroup>
        <PreferenceField label="Availability">
          <div className="flex flex-wrap gap-2">
            {availabilityOptions.map((option) => (
              <ChoiceChip key={option.id} selected={availability === option.id} onClick={() => onAvailabilityChange(option.id)}>
                {option.label}
              </ChoiceChip>
            ))}
          </div>
        </PreferenceField>

        <PreferenceField label="Workspace Type">
          <TagChoices options={options.workspaceType} values={value.workspaceTypes} onChange={(next) => update('workspaceTypes', next)} />
        </PreferenceField>

        <PreferenceField label="Seniority Level">
          <SearchPicker options={options.seniorityLevel} values={value.seniorityLevels} onChange={(next) => update('seniorityLevels', next)} />
        </PreferenceField>
        <PreferenceField label="Job Title(s)">
          <SearchPicker options={options.jobTitle} values={value.jobTitles} onChange={(next) => update('jobTitles', next)} />
        </PreferenceField>
        <PreferenceField label="Skills">
          <SearchPicker
            options={options.skill}
            values={value.skills.map((skill) => skill.skillName)}
            onChange={(next) => update('skills', next.flatMap((skillName) => {
              const option = options.skill.find((item) => item.label === skillName)
              return option ? [{ skillId: option.id, skillName: option.label }] : []
            }))}
          />
        </PreferenceField>
      </PreferenceGroup> : null}

      {section === 'about-job' ? <PreferenceGroup>
        <PreferenceField label="Job Type">
          <TagChoices options={options.jobType} values={value.jobTypes} onChange={(next) => update('jobTypes', next)} />
        </PreferenceField>
        <div className="grid gap-5 sm:grid-cols-[1fr_auto]">
          <PreferenceField label="Desired Salary">
            <Input type="number" min="0" value={value.desiredSalary} onChange={(event) => update('desiredSalary', event.target.value)} placeholder="Enter monthly salary" />
          </PreferenceField>
          <PreferenceField label="Currency">
            <div className="flex flex-wrap gap-2">
              {options.currency.map((option) => <ChoiceChip key={option.id} selected={value.currency === option.label} onClick={() => update('currency', option.label)}>{option.label}</ChoiceChip>)}
            </div>
          </PreferenceField>
        </div>
        <PreferenceField label="Desired Location">
          <SearchPicker
                values={value.desiredLocations}
                onChange={(next) => update('desiredLocations', next)}
                onSearch={(q) => getCities({ q }).then(citiesToOptions)}
                onResultsSeen={onLocationResultsSeen}
                placeholder="Type a city name…"
              />
        </PreferenceField>
      </PreferenceGroup> : null}

      {section === 'about-company' ? <PreferenceGroup>
        <PreferenceField label="Company Size">
          <SearchPicker options={options.companySize} values={value.companySizes} onChange={(next) => update('companySizes', next)} />
        </PreferenceField>
        <PreferenceField label="Industry">
          <SearchPicker options={options.industry} values={value.industries} onChange={(next) => update('industries', next)} />
        </PreferenceField>
      </PreferenceGroup> : null}
    </div>
  )
}

function PreferenceGroup({ children }: { children: React.ReactNode }) {
  return <div className="space-y-6">{children}</div>
}

function PreferenceField({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><p className="mb-2.5 text-sm font-semibold text-[#555]">{label}</p>{children}</div>
}

function TagChoices({ options, values, onChange }: { options: PreferenceOption[]; values: string[]; onChange(values: string[]): void }) {
  return <div className="flex flex-wrap gap-2">{options.map((option) => <ChoiceChip key={option.id} selected={values.includes(option.label)} onClick={() => onChange(toggle(values, option.label))}>{option.label}</ChoiceChip>)}</div>
}

function ChoiceChip({ selected, onClick, children }: { selected: boolean; onClick(): void; children: React.ReactNode }) {
  return <button type="button" onClick={onClick} className={`inline-flex min-h-9 items-center gap-1.5 rounded-full border px-4 py-2 text-sm transition ${selected ? 'border-accent bg-accent text-white' : 'border-border bg-white text-[#555] hover:border-accent hover:text-primary'}`}>{selected ? <Check className="size-3.5" /> : null}{children}</button>
}

function toggle(values: string[], value: string) {
  return values.includes(value) ? values.filter((item) => item !== value) : [...values, value]
}
